import type {TimelineEntry} from './hooks'

/** Ordered sections for rendering v2 assistant timelines (preamble inline; tools/reasoning in Working). */
export type TimelineRenderSection =
  | {kind: 'preamble'; content: string}
  | {kind: 'working'; entries: TimelineEntry[]}

/**
 * Walk `timeline.entries` in order.
 * - Any **text** before the first tool row is shown as preamble (even if tagged `reasoning`).
 * - After the first tool, text + tools stay in Working buckets.
 * - `segment_kind: final` is omitted (answer is `message.content`).
 */
export function partitionTimelineIntoOrderedSections(entries: TimelineEntry[]): TimelineRenderSection[] {
  const sections: TimelineRenderSection[] = []
  const workingBuffer: TimelineEntry[] = []
  let seenTool = false

  const flushWorking = () => {
    if (workingBuffer.length === 0) return
    sections.push({kind: 'working', entries: workingBuffer.splice(0, workingBuffer.length)})
  }

  const appendPreamble = (content: string) => {
    const last = sections[sections.length - 1]
    if (last?.kind === 'preamble') {
      last.content = `${last.content}\n\n${content}`.trim()
    } else {
      sections.push({kind: 'preamble', content})
    }
  }

  for (const e of entries) {
    if (e.kind === 'text' && e.segment_kind === 'final') {
      continue
    }

    if (e.kind === 'tool_call' || e.kind === 'tool_result') {
      seenTool = true
      workingBuffer.push(e)
      continue
    }

    if (e.kind === 'text') {
      if (!seenTool) {
        flushWorking()
        appendPreamble(e.content)
      } else {
        workingBuffer.push(e)
      }
      continue
    }

    workingBuffer.push(e)
  }
  flushWorking()
  return sections
}
